import { findNode } from './findNode'
import { NodeEqualityFn, TreeNode, TreeProps } from './interface'

export class Tree<T extends TreeNode<T>> {
  public getData: () => T[]
  public setData: (data: T[]) => void
  private equalityFn: NodeEqualityFn<T>

  constructor({ getData, setData, equalityFn }: TreeProps<T>) {
    this.getData = getData
    this.setData = setData
    this.equalityFn = equalityFn

    this.insertNode = this.insertNode.bind(this)
    this.moveNode = this.moveNode.bind(this)
    this.removeNode = this.removeNode.bind(this)
    this.updateNode = this.updateNode.bind(this)
  }

  private get data() {
    return this.getData()
  }

  private set data(data: T[]) {
    this.setData(data)
  }

  private triggerUpdate() {
    this.data = this.data.slice()
  }

  /**
   * 插入新的节点到树中，默认插入到父节点的末尾
   *
   * @param node 要插入的节点
   * @param parent 要插入的父节点
   * @param index 要插入的位置
   */
  insertNode(node: T, parent?: T, index?: number) {
    let siblings = this.data
    if (parent) {
      // 需要找到 parent 节点进行副作用操作
      const { node: parentNode } = findNode(this.data, value =>
        this.equalityFn(value, parent)
      )
      if (parentNode) {
        if (!parentNode.children) {
          parentNode.children = []
        }
        siblings = parentNode.children
      }
    }
    siblings.splice(index ?? siblings.length, 0, node)
    this.triggerUpdate()
  }

  /**
   * 从树中移除某个节点
   *
   * @param dept 要移除的节点
   */
  removeNode(node: T) {
    const { siblings, index } = findNode(this.data, value =>
      this.equalityFn(value, node)
    )
    if (siblings) {
      siblings.splice(index, 1)
    }
    this.triggerUpdate()
  }

  /**
   * 移动树中的某个节点, 移动时默认插入到父节点的末尾
   *
   * @param source 原节点
   * @param parent 要移到哪个节点下面
   * @param index 在父节点中的位置
   */
  moveNode(source: T, parent?: T, index?: number) {
    this.removeNode(source)
    this.insertNode(source, parent, index)
  }

  /**
   * 更新某个节点的信息
   *
   * @param node 要更新的节点
   */
  updateNode(node: T) {
    const { node: target, siblings } = findNode(this.data, value =>
      this.equalityFn(value, node)
    )
    if (!target) {
      return
    }
    const index = siblings.indexOf(target)
    siblings.splice(index, 1, node as any)
    this.triggerUpdate()
  }

  /**
   * 查找某个节点的父节点、兄弟节点、在父节点中的位置等信息
   *
   * @param node 目标节点
   */
  findNode(node: T) {
    return findNode(this.data, value => this.equalityFn(value, node))
  }
}
